import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { TokenIcon } from '@web3icons/react/dynamic';
import * as React from 'react';

import Card from 'components/card/Card';
import { formatPrice } from 'utils/number';
import {
  RowObj,
  TRANSACTION_TYPE,
} from '../variables/tableHistoryTransactionData';
import dayjs from 'dayjs';

const columnHelper = createColumnHelper<RowObj>();

export default function HistoryTransaction(props: { tableData: RowObj[] }) {
  const { tableData } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = [
    // TRANSACTION DATE
    // TYPE
    columnHelper.accessor('transactionDate', {
      id: 'transactionDate',
      header: () => <Text color="gray.400">TIME</Text>,
      cell: (info) => (
        <Flex align="center" gap="8px">
          <Text>{dayjs(info.getValue()).format('DD/MM/YYYY HH:mm')}</Text>
        </Flex>
      ),
    }),
    // TYPE
    columnHelper.accessor('type', {
      id: 'type',
      header: () => <Text color="gray.400">TYPE</Text>,
      cell: (info) => (
        <Badge
          colorScheme={
            info.getValue() === TRANSACTION_TYPE.BUY ? 'green' : 'red'
          }
        >
          {info.getValue().toUpperCase()}
        </Badge>
      ),
    }),

    // FROM
    columnHelper.accessor('fromCoin', {
      id: 'from',
      header: () => <Text color="gray.400">FROM</Text>,
      cell: ({ row }) => (
        <Flex align="center" gap="8px">
          <TokenIcon
            symbol={row.original.fromCoin}
            size={30}
            variant="branded"
          />
          <Text>
            {row.original.fromVolume} {row.original.fromCoin}
          </Text>
        </Flex>
      ),
    }),

    // TO
    columnHelper.accessor('toCoin', {
      id: 'to',
      header: () => <Text color="gray.400">TO</Text>,
      cell: ({ row }) => (
        <Flex align="center" gap="8px">
          <TokenIcon symbol={row.original.toCoin} size={30} variant="branded" />
          <Text>
            {row.original.toVolume} {row.original.toCoin}
          </Text>
        </Flex>
      ),
    }),

    // VALUE (USDT)
    columnHelper.display({
      id: 'value',
      header: () => (
        <Flex justify="center">
          <Text color="gray.400">VALUE (USDT)</Text>
        </Flex>
      ),
      cell: ({ row }) => {
        const isBuy = row.original.type === TRANSACTION_TYPE.BUY;
        const value = isBuy ? row.original.fromVolume : row.original.toVolume;

        return (
          <Flex justify="center">
            <Text color={isBuy ? 'green.400' : 'red.400'} fontWeight="700">
              {formatPrice(value)}
            </Text>
          </Flex>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Card flexDirection="column" w="100%" px="0px">
      <Flex px="25px" mb="8px">
        <Text fontSize="22px" fontWeight="700" color={textColor}>
          Transaction History
        </Text>
      </Flex>

      <Box>
        <Table variant="simple">
          <Thead>
            {table.getHeaderGroups().map((hg) => (
              <Tr key={hg.id}>
                {hg.headers.map((header) => (
                  <Th
                    key={header.id}
                    borderColor={borderColor}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor="pointer"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} borderColor="transparent">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
